import { Link } from 'react-router-dom'

import planetLogo from '@/assets/brand/nibiru-planet-logo.png'
import planetLogoRed from '@/assets/brand/nibiru-planet-logo-red.png'

export type BrandLogoSize = 'sm' | 'md' | 'lg'
export type BrandLogoTone = 'default' | 'light' | 'auth-ember'

export interface BrandLogoProps {
	linkTo?: string
	compact?: boolean
	size?: BrandLogoSize
	tone?: BrandLogoTone
	className?: string
	titleClassName?: string
	subtitleClassName?: string
	/** When false, renders a div instead of Link (e.g. inside another link). */
	asLink?: boolean
}

const markHeights: Record<BrandLogoSize, string> = {
	sm: 'h-8',
	md: 'h-11',
	lg: 'h-12',
}

const titleSizes: Record<BrandLogoSize, string> = {
	sm: 'text-[0.9rem]',
	md: 'text-base',
	lg: 'text-lg',
}

const subtitleSizes: Record<BrandLogoSize, string> = {
	sm: 'text-[9px] tracking-[0.2em]',
	md: 'text-[10px] tracking-[0.22em]',
	lg: 'text-[11px] tracking-[0.24em]',
}

const sharedNexusAccentClass = 'text-primary'

const toneClasses: Record<BrandLogoTone, { title: string; subtitle: string; accent: string }> = {
	default: {
		title: 'text-foreground',
		subtitle: 'text-muted-foreground',
		accent: sharedNexusAccentClass,
	},
	light: {
		title: 'text-white',
		subtitle: 'text-[hsl(352_24%_66%)]',
		accent: sharedNexusAccentClass,
	},
	'auth-ember': {
		title: 'text-white',
		subtitle: 'text-[#d3a097]',
		accent: sharedNexusAccentClass,
	},
}

const BrandLogo = ({
	linkTo = '/',
	compact = false,
	size = 'md',
	tone = 'light',
	className = '',
	titleClassName,
	subtitleClassName,
	asLink = true,
}: BrandLogoProps) => {
	const colors = toneClasses[tone]
	// 'default' tone is used on light backgrounds (e.g. light sidebar theme) — the white
	// mark relies on mix-blend-screen, which washes out to invisible on a light background,
	// so use the solid red mark there instead, without the dark-background blend mode.
	const isLightBackground = tone === 'default'
	const markSrc = isLightBackground ? planetLogoRed : planetLogo
	const markBlendClass = isLightBackground ? '' : 'mix-blend-screen'
	const content = (
		<>
			<span className="brand-logo__mark relative inline-flex shrink-0 items-center justify-center">
				<span
					className="pointer-events-none absolute -inset-1.5 rounded-full bg-[radial-gradient(circle,hsl(357_91%_59%_/0.34),transparent_72%)] opacity-70 blur-[18px]"
					aria-hidden
				/>
				<img
					src={markSrc}
					alt=""
					className={`brand-logo__image relative w-auto object-contain ${markBlendClass} ${markHeights[size]}`}
				/>
			</span>
			{!compact ? (
				<span className="brand-logo__wordmark flex min-w-0 flex-col leading-none">
					<span
						className={
							titleClassName ??
							`font-brand font-bold tracking-[-0.03em] ${titleSizes[size]} ${colors.title}`
						}
					>
						NIBIRU<span className={colors.accent}>NEXUS</span>
					</span>
					<span
						className={
							subtitleClassName ??
							`font-brand-sub mt-[0.22rem] font-medium uppercase ${subtitleSizes[size]} ${colors.subtitle}`
						}
					>
						{tone === 'auth-ember' ? 'BY NIBIRU SOFTWARE INC' : 'by Nibiru Software Inc'}
					</span>
				</span>
			) : null}
		</>
	)

	const rootClass = `brand-logo inline-flex items-center gap-3.5 no-underline transition-opacity hover:opacity-90 ${className}`.trim()

	if (!asLink) {
		return <div className={rootClass}>{content}</div>
	}

	return (
		<Link to={linkTo} className={rootClass} aria-label="SQLDBD by Nibiru — home">
			{content}
		</Link>
	)
}

export default BrandLogo
